import { getTodayInTimezone } from './dateUtils';

// In development, use the same hostname the browser is on (supports iPad/LAN access)
//
// Port 3001 is the backend's default. To point at a different one — a second
// stack, or a machine where 3001 is taken — set REACT_APP_API_URL in
// frontend/.env.local (see frontend/.env.local.example). Any value other than
// the literal http://localhost:3001 wins over the hostname sniffing below.
const getDevBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    return `http://${window.location.hostname}:3001`;
  }
  return 'http://localhost:3001';
};
const BASE_URL = process.env.REACT_APP_API_URL === 'http://localhost:3001' || !process.env.REACT_APP_API_URL
  ? getDevBaseUrl()
  : process.env.REACT_APP_API_URL;

// Add /api prefix for production deployment
const API_PREFIX = process.env.NODE_ENV === 'production' && !process.env.REACT_APP_API_URL?.includes('/api') ? '/api' : '';

// The API root as a browser on another site would call it (embed snippets).
export const PUBLIC_API_ROOT = `${BASE_URL}${API_PREFIX}`;

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
};

/** The person picked in the staff selector (shared staff login), or null. */
export const getSelectedStaffMember = () => {
  try {
    const saved = localStorage.getItem('selectedStaffMember');
    return saved ? JSON.parse(saved) : null;
  } catch (e) {
    return null;
  }
};

// Token refresh state to prevent multiple simultaneous refresh attempts
let isRefreshingToken = false;
let tokenRefreshPromise = null;

/**
 * Decode JWT token to get expiry time
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
const decodeToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

/**
 * Check if token is expired or about to expire
 * @param {string} token - JWT token
 * @param {number} bufferSeconds - Seconds before expiry to consider token as expiring (default: 5 minutes)
 * @returns {boolean} True if token is expired or about to expire
 */
const isTokenExpiring = (token, bufferSeconds = 300) => {
  if (!token) return true;

  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const expiryTime = decoded.exp * 1000; // Convert to milliseconds
  const now = Date.now();
  const bufferMs = bufferSeconds * 1000;

  return now >= (expiryTime - bufferMs);
};

/**
 * Proactively refresh token if it's about to expire
 * Prevents the user from being logged out mid-operation
 */
const ensureValidToken = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  // If token is not expiring, no action needed
  if (!isTokenExpiring(token)) {
    return token;
  }

  // Token is expiring soon, need to refresh
  console.log('[Auth] Token expiring soon, refreshing...');

  // If already refreshing, wait for that to complete
  if (isRefreshingToken && tokenRefreshPromise) {
    return tokenRefreshPromise;
  }

  // Start refresh
  isRefreshingToken = true;
  tokenRefreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}${API_PREFIX}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('[Auth] Token refresh failed:', errorData.message);
        // Don't clear token here - let the request fail and AuthContext handle logout
        return token;
      }

      const data = await response.json();
      if (data.success && data.data?.token) {
        localStorage.setItem('token', data.data.token);
        console.log('[Auth] Token refreshed successfully');
        return data.data.token;
      }

      return token;
    } catch (error) {
      console.error('[Auth] Token refresh error:', error);
      return token;
    } finally {
      isRefreshingToken = false;
      tokenRefreshPromise = null;
    }
  })();

  return tokenRefreshPromise;
};

// Login header for calls that can't go through makeRequest (form uploads,
// raw fetches, file downloads).
export const authHeaders = async () => {
  const token = await ensureValidToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Open a file the API only serves with a login (PDFs). A plain link can't
// send the header, so fetch it and open the downloaded copy.
export const openAuthenticatedFile = async (url) => {
  const res = await fetch(url, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Download failed: HTTP ${res.status}`);
  const fileUrl = URL.createObjectURL(await res.blob());
  window.open(fileUrl, '_blank');
  setTimeout(() => URL.revokeObjectURL(fileUrl), 60000);
};

// A suspended shop's session is refused with 403 SHOP_SUSPENDED on any call
// (backend middleware/auth.js, 10B). makeRequest announces it with this window
// event; AuthContext signs out and the sign-in screen shows the message.
export const SHOP_SUSPENDED_EVENT = 'bouqmark:shop-suspended';

// A driver signed in with a one-time password (token claim `pwChange`) is
// refused with 403 PASSWORD_CHANGE_REQUIRED until they pick their own, which
// only the Driver app offers. makeRequest announces it with this event;
// AuthContext marks the session and DriverPasswordChangeGate takes over.
export const PASSWORD_CHANGE_REQUIRED_EVENT = 'bouqmark:password-change-required';

/** True when the stored session is a driver's one-time-password session. */
export const tokenRequiresPasswordChange = () => {
  const token = localStorage.getItem('token');
  const decoded = token ? decodeToken(token) : null;
  return Boolean(decoded && decoded.accountType === 'driver' && decoded.pwChange === true);
};

class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const makeRequest = async (url, options = {}, retryCount = 0, skipTokenRefresh = false) => {
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second

  try {
    // Add Authorization header if token exists
    let token = localStorage.getItem('token');

    // Proactively refresh token if it's about to expire (skip for auth endpoints to avoid loops)
    if (token && !skipTokenRefresh && !url.includes('/auth/refresh') && !url.includes('/auth/login')) {
      token = await ensureValidToken();
    }

    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    // Include selected staff member for timeline attribution (name) and, on the
    // shared staff login, for per-person 2FA / card access (id).
    const staffHeaders = {};
    const staff = getSelectedStaffMember();
    if (staff) {
      if (staff.firstName && staff.lastName) {
        staffHeaders['X-Staff-Member'] = `${staff.firstName} ${staff.lastName}`;
      }
      if (staff.id) {
        staffHeaders['X-Staff-Member-Id'] = staff.id;
      }
    }

    const config = {
      ...options,
      headers: {
        ...DEFAULT_HEADERS,
        ...authHeaders,
        ...staffHeaders,
        ...options.headers,
      },
    };

    console.log(`[API] ${config.method || 'GET'} ${url}`, config.body ? JSON.parse(config.body) : '');

    const response = await fetch(`${BASE_URL}${API_PREFIX}${url}`, config);
    
    if (!response.ok) {
      let errorData = null;
      
      try {
        errorData = await response.json();
      } catch (parseError) {
        // If response is not JSON, use statusText
        errorData = { message: response.statusText };
      }

      const errorMessage = errorData?.message || `HTTP ${response.status}: ${response.statusText}`;
      // Sign-in answers for itself; any other call means the open session is over.
      if (response.status === 403 && errorData?.code === 'SHOP_SUSPENDED' && !url.includes('/auth/login')) {
        window.dispatchEvent(new CustomEvent(SHOP_SUSPENDED_EVENT, { detail: { message: errorMessage } }));
      }
      if (response.status === 403 && errorData?.code === 'PASSWORD_CHANGE_REQUIRED') {
        window.dispatchEvent(new CustomEvent(PASSWORD_CHANGE_REQUIRED_EVENT));
      }
      throw new ApiError(errorMessage, response.status, errorData);
    }

    const data = await response.json();
    console.log(`[API] Response:`, data);
    return data;

  } catch (error) {
    // Handle 401 errors - try to refresh token and retry once
    if (error instanceof ApiError && error.status === 401 && !skipTokenRefresh && !url.includes('/auth/')) {
      console.log('[API] 401 received, attempting token refresh and retry...');
      try {
        await ensureValidToken();
        // Retry with new token (but skip refresh to prevent infinite loop)
        return makeRequest(url, options, retryCount, true);
      } catch (refreshError) {
        console.error('[API] Token refresh failed, cannot retry request');
        throw error;
      }
    }

    // Handle network errors and other non-HTTP errors
    if (error instanceof ApiError) {
      throw error; // Re-throw API errors as-is
    }

    // Network error or other fetch error
    if (retryCount < maxRetries && shouldRetry(error)) {
      console.warn(`[API] Request failed, retrying in ${retryDelay}ms... (attempt ${retryCount + 1}/${maxRetries})`);
      await sleep(retryDelay * (retryCount + 1)); // Exponential backoff
      return makeRequest(url, options, retryCount + 1, skipTokenRefresh);
    }

    // Convert network errors to ApiError
    const message = error.message || 'Network error';
    throw new ApiError(message, 0, { originalError: error.message });
  }
};

const shouldRetry = (error) => {
  // Retry on network errors, timeouts, and 5xx server errors
  if (error instanceof ApiError) {
    return error.status >= 500 || error.status === 0;
  }
  
  // Retry on network errors
  return error.name === 'TypeError' && error.message.includes('fetch');
};

// Orders API
export const ordersApi = {
  // Get all orders with pagination
  getAll: (options = {}) => {
    const { limit = 100, page = 1 } = options;
    return makeRequest(`/orders?limit=${limit}&page=${page}`);
  },

  // Get order statistics
  getStats: () => makeRequest('/orders/stats'),

  // Get order by ID
  getById: (id) => makeRequest(`/orders/${id}`),

  // Create new order
  create: (orderData) => makeRequest('/orders', {
    method: 'POST',
    body: JSON.stringify(orderData),
  }),

  // How this shop's staff orders are paid: { paymentMode: 'shopify' | 'counter' }
  getEntryConfig: () => makeRequest('/orders/entry-config'),

  // Create draft order in Shopify
  createDraft: (draftData) => makeRequest('/orders/create-draft', {
    method: 'POST',
    body: JSON.stringify(draftData),
  }),

  // Update order
  update: (id, updateData) => makeRequest(`/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  }),

  // Update order status
  updateStatus: (id, status) => makeRequest(`/orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  }),

  // Mark order as paid (for pay later orders)
  markAsPaid: (id) => makeRequest(`/orders/${id}/mark-paid`, {
    method: 'PUT',
  }),

  // Mark order as delivered (manual delivery confirmation)
  markAsDelivered: (id, data) => makeRequest(`/orders/${id}/mark-delivered`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // Reset delivery status for redelivery
  resetDelivery: (id, reason) => makeRequest(`/orders/${id}/reset-delivery`, {
    method: 'PUT',
    body: JSON.stringify({ reason }),
  }),

  // Complete delivery verification
  completeDeliveryVerification: (id, verificationData) => makeRequest(`/orders/${id}/delivery-verification`, {
    method: 'PUT',
    body: JSON.stringify(verificationData),
  }),

  // Scan-to-ready: look up order by order number
  lookupByOrderNumber: (orderNumber) => makeRequest(`/orders/lookup/${encodeURIComponent(orderNumber)}`),

  // Scan-to-ready: mark arrangement as scanned/complete. `via` labels the
  // order history entry ('ready_button' for Mark ready for pickup).
  scanComplete: (id, via) => makeRequest(`/orders/${id}/scan-complete`, {
    method: 'POST',
    ...(via ? { body: JSON.stringify({ via }) } : {}),
  }),

  // Scan-to-ready: clear arrangement scan
  unscan: (id) => makeRequest(`/orders/${id}/unscan`, { method: 'POST' }),

  // Scan-to-ready: quick delivery verification from scan station
  quickVerify: (id, notes) => makeRequest(`/orders/${id}/quick-verify`, {
    method: 'POST',
    body: JSON.stringify({ notes }),
  }),

  // Manually attach the Mother's Day 2026 complimentary gift to an order.
  // Backend validates eligibility + inventory — returns 400 / 409 if ineligible.
  addMothersDayGift: (id) => makeRequest(`/orders/${id}/add-mothers-day-gift`, {
    method: 'POST',
  }),

  // Delete order
  delete: (id) => makeRequest(`/orders/${id}`, {
    method: 'DELETE',
  }),

  // Get order timeline/history
  // Every text and email for an order (sent, scheduled, skipped, failed), newest first
  getMessages: (id) => makeRequest(`/orders/${encodeURIComponent(id)}/messages`),
  // Text a customer back after they texted in. { body, audience }
  textBack: (id, payload) => makeRequest(`/orders/${encodeURIComponent(id)}/text-back`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),

  getTimeline: (id, options = {}) => {
    const { limit = 50, offset = 0 } = options;
    const queryParams = new URLSearchParams({ limit, offset });
    return makeRequest(`/orders/${id}/timeline?${queryParams}`);
  },

  // Add a free-text staff comment to the order timeline
  addTimelineComment: (id, comment) => makeRequest(`/orders/${id}/timeline/comment`, {
    method: 'POST',
    body: JSON.stringify({ comment }),
  }),

  // HTML of an email-sent timeline event's email. Fetched with the login
  // header (an iframe src can't send one) and shown via srcDoc.
  emailPreviewHtml: async (id, eventType) => {
    const token = await ensureValidToken();
    const res = await fetch(
      `${BASE_URL}${API_PREFIX}/orders/${encodeURIComponent(id)}/email-preview/${encodeURIComponent(eventType)}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!res.ok) throw new ApiError(`Email preview failed: HTTP ${res.status}`, res.status);
    return res.text();
  },

  // Sync customer email to Shopify order
  syncShopifyEmail: (id, email) => makeRequest(`/orders/${id}/sync-shopify-email`, {
    method: 'PUT',
    body: JSON.stringify({ email }),
  }),

  // Retry failed Shopify sync using current ROSA state
  retryShopifySync: (id) => makeRequest(`/orders/${id}/retry-shopify-sync`, {
    method: 'POST',
  }),

  // Preview order confirmation email HTML
  previewConfirmationEmail: (id) => makeRequest(`/orders/${id}/preview-confirmation-email`),

  // Send order confirmation email via Resend
  sendConfirmationEmail: (id, email) => makeRequest(`/orders/${id}/send-confirmation-email`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  }),

  // Generate PDF report for deliveries or pickups
  generateReport: async (type, startDate, endDate, sortBy = null, forceGenerate = false) => {
    const params = new URLSearchParams({
      type,
      startDate,
      endDate
    });
    if (sortBy) {
      params.append('sortBy', sortBy);
    }
    if (forceGenerate) {
      params.append('forceGenerate', 'true');
    }
    const response = await makeRequest(`/reports/orders?${params}`);
    return response;
  },

  // Sales tax collected by channel (owner/admin), orders placed from..to
  salesTaxReport: (from, to) => makeRequest(`/reports/sales-tax?${new URLSearchParams({ from, to })}`),
};

// Tenant onboarding funnel (Getting Started checklist) — owner/admin only.
export const onboardingApi = {
  get: () => makeRequest('/onboarding'),
  setStep: (stepKey, status) => makeRequest(`/onboarding/${encodeURIComponent(stepKey)}`, { method: 'PUT', body: JSON.stringify({ status }) }),
};

// The shop's acceptance of the Bouqmark terms (clickwrap). Any staff reads; owner/admin accepts.
export const termsApi = {
  get: () => makeRequest('/terms-acceptance'),
  accept: ({ termsVersion, dpaVersion }) => makeRequest('/terms-acceptance', {
    method: 'POST',
    body: JSON.stringify({ agree: true, termsVersion, dpaVersion }),
  }),
};

// Self-serve trial sign-up (10B). Public: no login needed.
export const signupApi = {
  start: (form) => makeRequest('/signup', { method: 'POST', body: JSON.stringify(form) }),
  verify: (token) => makeRequest('/signup/verify', { method: 'POST', body: JSON.stringify({ token }) }),
};

// The signed-in shop's name, logo and pickup address. Any signed-in user.
export const shopIdentityApi = {
  get: () => makeRequest('/shop-identity'),
};

// The shop's plan, limits and monthly order count (Studio S4). Admins only.
export const planApi = {
  get: () => makeRequest('/plan'),
};

// The shop's subscription to Bouqmark (billing B2/B3, Settings → Plan & billing).
// get: owner or admin (read-only); everything else is owner only.
//   checkout → { id, url } (Stripe Checkout)   portal → { url }
//   previewAddOns → { changed, prorationCents, nextInvoiceCents, nextBillAt, prorationDate }
//   changeAddOns → the get() summary after the change
//   cancelYearlyQuote → { refundCents, amountPaidCents, monthsStarted, usedCents, perksCents }
export const subscriptionApi = {
  get: () => makeRequest('/billing'),
  checkout: (body) => makeRequest('/billing/checkout', { method: 'POST', body: JSON.stringify(body) }),
  // "Have a code?": what the code takes off that checkout, before it opens.
  previewCode: (body) => makeRequest('/billing/code/preview', { method: 'POST', body: JSON.stringify(body) }),
  portal: () => makeRequest('/billing/portal', { method: 'POST', body: JSON.stringify({}) }),
  // The oldest unpaid bill's Stripe page (QA B P1-4): { url, amountDueCents, ... }
  payNow: () => makeRequest('/billing/pay-now', { method: 'POST', body: JSON.stringify({}) }),
  previewAddOns: (addOns) => makeRequest('/billing/add-ons/preview', { method: 'POST', body: JSON.stringify({ addOns }) }),
  changeAddOns: (addOns, prorationDate) => makeRequest('/billing/add-ons', {
    method: 'POST',
    body: JSON.stringify(prorationDate ? { addOns, prorationDate } : { addOns }),
  }),
  cancelYearlyQuote: () => makeRequest('/billing/cancel-yearly'),
  cancelYearly: (expectedRefundCents) => makeRequest('/billing/cancel-yearly', {
    method: 'POST',
    body: JSON.stringify({ expectedRefundCents }),
  }),
  // B3b: switch plan and/or monthly ↔ yearly in the app (the portal can't
  // change a subscription with more than one product).
  //   pendingSwitch → { pending: { plan, interval, addOns, effectiveAt } | null }
  //   previewSwitch → { changed, kind, …, expectedCents, prorationDate }
  //   switchPlan → { switch, billing: the get() summary after the switch }
  pendingSwitch: () => makeRequest('/billing/switch'),
  previewSwitch: (target) => makeRequest('/billing/switch/preview', { method: 'POST', body: JSON.stringify(target) }),
  switchPlan: (target, expectedCents, prorationDate) => makeRequest('/billing/switch', {
    method: 'POST',
    body: JSON.stringify({ ...target, expectedCents, ...(prorationDate ? { prorationDate } : {}) }),
  }),
};

// Settings → Payments (Online store 4B): the shop's own Stripe account.
// Connect and onboarding links are owner-only; the account session is owner/admin.
export const stripeConnectApi = {
  status: ({ refresh = false } = {}) => makeRequest(`/stripe/connect/status${refresh ? '?refresh=1' : ''}`),
  createAccount: () => makeRequest('/stripe/connect/account', { method: 'POST' }),
  accountLink: () => makeRequest('/stripe/connect/account-link', { method: 'POST' }),
  accountSession: () => makeRequest('/stripe/account-session', { method: 'POST', body: JSON.stringify({}) }),
  config: () => makeRequest('/stripe/config'),
};

// Card payments on staff orders for Stripe shops (audit R1). Refunds are admin/owner-only.
export const orderPaymentsApi = {
  config: () => makeRequest('/order-payments/config'),
  get: (orderId) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}`),
  cardIntent: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/card-intent`, { method: 'POST', body: JSON.stringify(body) }),
  // Pay links: { amount? } makes one (the whole balance without it); send takes
  // { channel, amount? } (makes it too) or { channel, payment_id } for an open link.
  payLink: (orderId, amount = null) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/pay-link`, { method: 'POST', body: JSON.stringify(amount !== null && amount !== undefined ? { amount } : {}) }),
  sendPayLink: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/pay-link/send`, { method: 'POST', body: JSON.stringify(typeof body === 'string' ? { channel: body } : body) }),
  cancelPayLink: (orderId, paymentId) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/pay-link/cancel`, { method: 'POST', body: JSON.stringify({ payment_id: paymentId }) }),
  chargeSavedCard: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/saved-card`, { method: 'POST', body: JSON.stringify(body) }),
  refund: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/refund`, { method: 'POST', body: JSON.stringify(body) }),
  // Counter card reader (audit R2): { attempt_key, reader_id } / { payment_id }.
  chargeOnReader: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/reader`, { method: 'POST', body: JSON.stringify(body) }),
  cancelReader: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/reader/cancel`, { method: 'POST', body: JSON.stringify(body) }),
  // Gift cards as a tender (Online store 4C): { code } / { code, attempt_key, amount? }.
  checkGiftCard: (orderId, code) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/gift-card/check`, { method: 'POST', body: JSON.stringify({ code }) }),
  redeemGiftCard: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/gift-card`, { method: 'POST', body: JSON.stringify(body) }),
  // Cash register tenders (no card processor needed): { attempt_key, tendered, register_id? },
  // { attempt_key, check_number, amount?, register_id? }; other is admin/owner-only { attempt_key, reason }.
  cash: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/cash`, { method: 'POST', body: JSON.stringify(body) }),
  check: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/check`, { method: 'POST', body: JSON.stringify(body) }),
  other: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/other`, { method: 'POST', body: JSON.stringify(body) }),
  // The receipt on the shop's receipt printer (Bouqmark Print Client):
  // { payment_id, reason: 'sale' } after a payment, { reason: 'reprint' } any time → { sent, reason }.
  receipt: (orderId, body) => makeRequest(`/order-payments/${encodeURIComponent(orderId)}/receipt`, { method: 'POST', body: JSON.stringify(body) }),
};

// Card on file (COF-2): one saved card per customer on the shop's Stripe account.
// get → { available, hasEmail, houseAccount, card, linkHours }; request emails the
// customer a Stripe link; setupIntent { attempt_key } + confirm { setup_intent_id }
// are staff entry with Stripe's Payment Element; remove detaches it.
export const cardOnFileApi = {
  get: (customerId) => makeRequest(`/card-on-file/${encodeURIComponent(customerId)}`),
  request: (customerId) => makeRequest(`/card-on-file/${encodeURIComponent(customerId)}/request`, { method: 'POST', body: JSON.stringify({}) }),
  setupIntent: (customerId, body) => makeRequest(`/card-on-file/${encodeURIComponent(customerId)}/setup-intent`, { method: 'POST', body: JSON.stringify(body) }),
  confirm: (customerId, body) => makeRequest(`/card-on-file/${encodeURIComponent(customerId)}/confirm`, { method: 'POST', body: JSON.stringify(body) }),
  remove: (customerId) => makeRequest(`/card-on-file/${encodeURIComponent(customerId)}`, { method: 'DELETE' }),
};

// Cash register: registers, sessions, drawer movements, blind count, close. Money is integer cents.
export const registersApi = {
  status: () => makeRequest('/registers'),
  create: (name) => makeRequest('/registers', { method: 'POST', body: JSON.stringify({ name }) }),
  update: (registerId, body) => makeRequest(`/registers/${encodeURIComponent(registerId)}`, { method: 'PATCH', body: JSON.stringify(body) }),
  open: (registerId, body) => makeRequest(`/registers/${encodeURIComponent(registerId)}/open`, { method: 'POST', body: JSON.stringify(body) }),
  session: (sessionId) => makeRequest(`/registers/sessions/${encodeURIComponent(sessionId)}`),
  movement: (sessionId, body) => makeRequest(`/registers/sessions/${encodeURIComponent(sessionId)}/movements`, { method: 'POST', body: JSON.stringify(body) }),
  count: (sessionId, body) => makeRequest(`/registers/sessions/${encodeURIComponent(sessionId)}/count`, { method: 'POST', body: JSON.stringify(body) }),
  approve: (sessionId, note) => makeRequest(`/registers/sessions/${encodeURIComponent(sessionId)}/approve`, { method: 'POST', body: JSON.stringify({ note }) }),
  close: (sessionId, body) => makeRequest(`/registers/sessions/${encodeURIComponent(sessionId)}/close`, { method: 'POST', body: JSON.stringify(body) }),
  report: (sessionId) => makeRequest(`/registers/sessions/${encodeURIComponent(sessionId)}/report`),
  history: (params = {}) => {
    const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
    return makeRequest(`/registers/sessions${q.toString() ? `?${q}` : ''}`);
  },
};

// The shop's Stripe Reader S700s (audit R2). Listing is for all staff; register/remove are admin/owner-only.
// Settings -> Blackout dates (routes/blackout-dates.js). The order form reads
// the public list through hooks/useDeliveryCalendar.
export const blackoutDatesApi = {
  list: () => makeRequest('/blackout-dates'),
  create: (body) => makeRequest('/blackout-dates', { method: 'POST', body: JSON.stringify(body) }),
  createMany: (body) => makeRequest('/blackout-dates/bulk', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => makeRequest(`/blackout-dates/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id) => makeRequest(`/blackout-dates/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  updateGroup: (groupId, body) => makeRequest(`/blackout-dates/group/${encodeURIComponent(groupId)}`, { method: 'PUT', body: JSON.stringify(body) }),
  removeGroup: (groupId) => makeRequest(`/blackout-dates/group/${encodeURIComponent(groupId)}`, { method: 'DELETE' }),
};

export const earlyClosuresApi = {
  list: () => makeRequest('/early-closures'),
  create: (body) => makeRequest('/early-closures', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => makeRequest(`/early-closures/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id) => makeRequest(`/early-closures/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const cardReadersApi = {
  list: () => makeRequest('/card-readers'),
  register: (body) => makeRequest('/card-readers', { method: 'POST', body: JSON.stringify(body) }),
  remove: (readerId) => makeRequest(`/card-readers/${encodeURIComponent(readerId)}`, { method: 'DELETE' }),
};

export const proposalsApi = {
  list: () => makeRequest('/proposals'),
  listByEvent: (eventId) => makeRequest(`/proposals/by-event/${encodeURIComponent(eventId)}`),
  get: (id) => makeRequest(`/proposals/${encodeURIComponent(id)}`),
  create: (body) => makeRequest('/proposals', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => makeRequest(`/proposals/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id) => makeRequest(`/proposals/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  send: (id, body) => makeRequest(`/proposals/${encodeURIComponent(id)}/send`, { method: 'POST', body: JSON.stringify(body) }),
};

// Wholesale vendor logins (Settings → Wholesale Vendors). Credentials are
// write-only: saved encrypted server-side and never returned to the browser.
export const vendorsApi = {
  list: () => makeRequest('/vendors'),
  create: (body) => makeRequest('/vendors', { method: 'POST', body: JSON.stringify(body) }),
  remove: (id) => makeRequest(`/vendors/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  update: (id, body) => makeRequest(`/vendors/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(body) }),
  saveCredentials: (id, body) => makeRequest(`/vendors/${encodeURIComponent(id)}/credentials`, { method: 'PUT', body: JSON.stringify(body) }),
  clearCredentials: (id) => makeRequest(`/vendors/${encodeURIComponent(id)}/credentials`, { method: 'DELETE' }),
  testConnection: (id) => makeRequest(`/vendors/${encodeURIComponent(id)}/test-connection`, { method: 'POST' }),
};

export const vendorPricesApi = {
  // Kick off a (background) live fetch + cache for a vendor + stems + date window.
  // Returns { job_id } — poll job() to render the live scrape progress bar.
  fetch: (body) => makeRequest('/vendor-prices/fetch', { method: 'POST', body: JSON.stringify(body) }),
  // Poll one fetch job's live progress: { status, step, step_detail, offers_count, error }.
  job: (id) => makeRequest(`/vendor-prices/job/${encodeURIComponent(id)}`),
  // Purge cached offers for a vendor/stem/date window (no re-fetch). Backs "Clear prices".
  clearPrices: ({ vendorId, stems, dateStart, dateEnd }) =>
    makeRequest('/vendor-prices/clear', {
      method: 'POST',
      body: JSON.stringify({ vendor_id: vendorId, stems, date_start: dateStart, date_end: dateEnd }),
    }),
  // Read cached offers only.
  offers: ({ vendorIds, stems, dateStart, dateEnd }) => {
    const p = new URLSearchParams();
    vendorIds.forEach((v) => p.append('vendor_ids', v));
    stems.forEach((s) => p.append('stems', s));
    p.append('date_start', dateStart);
    p.append('date_end', dateEnd);
    return makeRequest(`/vendor-prices/offers?${p.toString()}`);
  },
  // Curated substitute suggestions for a flower name: [{ substitute_key, note }].
  substitutes: (stem) =>
    makeRequest(`/vendor-prices/substitutes?stem=${encodeURIComponent(stem)}`),
};

// FormData upload for processing-intake bad-stem photos (Phase 7 slice 3).
export async function uploadIntakePhoto(intakeId, lineId, file) {
  const token = await ensureValidToken();
  const fd = new FormData();
  fd.append('photo', file);
  const res = await fetch(`${BASE_URL}${API_PREFIX}/production/intakes/${encodeURIComponent(intakeId)}/lines/${encodeURIComponent(lineId)}/photos`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) throw new ApiError(`Upload failed: HTTP ${res.status}`, res.status);
  return res.json();
}

// FormData upload — cannot use makeRequest (it forces JSON Content-Type).
export async function uploadProposalPhoto(id, file) {
  const token = await ensureValidToken();
  const fd = new FormData();
  fd.append('photo', file);
  const res = await fetch(`${BASE_URL}${API_PREFIX}/proposals/${encodeURIComponent(id)}/photos`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) throw new ApiError(`Upload failed: HTTP ${res.status}`, res.status);
  return res.json();
}

// FormData upload for event vision images (inspiration + dress photo).
export async function uploadVisionImage(eventId, file) {
  const token = await ensureValidToken();
  const fd = new FormData();
  fd.append('image', file);
  const res = await fetch(`${BASE_URL}${API_PREFIX}/events/${encodeURIComponent(eventId)}/vision/images`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  if (!res.ok) throw new ApiError(`Upload failed: HTTP ${res.status}`, res.status);
  return res.json(); // { success, url }
}

// Authenticated binary download — returns a Blob.
export async function downloadProposalPdf(id) {
  const token = await ensureValidToken();
  const res = await fetch(`${BASE_URL}${API_PREFIX}/proposals/${encodeURIComponent(id)}/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(`PDF failed: HTTP ${res.status}`, res.status);
  return res.blob();
}

// The sales tax report as CSV (owner/admin) — returns a Blob.
export async function downloadSalesTaxCsv(from, to) {
  const res = await fetch(`${BASE_URL}${API_PREFIX}/reports/sales-tax?${new URLSearchParams({ from, to, format: 'csv' })}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new ApiError(`CSV failed: HTTP ${res.status}`, res.status);
  return res.blob();
}

// Authenticated binary download of the consultation worksheet — returns a Blob.
export async function downloadWorksheetPdf(eventId) {
  const token = await ensureValidToken();
  const res = await fetch(`${BASE_URL}${API_PREFIX}/events/${encodeURIComponent(eventId)}/worksheet/pdf`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new ApiError(`Worksheet PDF failed: HTTP ${res.status}`, res.status);
  return res.blob();
}

export const publicProposalsApi = {
  get: (token) => makeRequest(`/proposals/public/${encodeURIComponent(token)}`),
  respond: (token, payload) => makeRequest(`/proposals/public/${encodeURIComponent(token)}/respond`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
};

// Public (no-auth) bride vision questionnaire, keyed by vision_token.
// Public (no-auth) booking packet (review + sign + pay), keyed by agreement_token.
// Waste log (SAF 2026 B1). The first three are the public QR form endpoints —
// the URL token is the only credential; the rest ride the normal staff JWT.
export const wasteApi = {
  ctx: (token) => makeRequest(`/waste/ctx/${encodeURIComponent(token)}`),
  createEntry: (token, payload) => makeRequest(`/waste/entries/${encodeURIComponent(token)}`, {
    method: 'POST', body: JSON.stringify(payload),
  }),
  amendEntry: (token, id, payload) => makeRequest(`/waste/entries/${encodeURIComponent(token)}/${id}`, {
    method: 'PATCH', body: JSON.stringify(payload),
  }),
  report: (days = 30) => makeRequest(`/waste/report?days=${days}`),
  entries: (days = 30, includeVoided = false) => makeRequest(`/waste/entries?days=${days}&includeVoided=${includeVoided}`),
  voidEntry: (id, reason) => makeRequest(`/waste/entries/${encodeURIComponent(id)}/void`, {
    method: 'POST', body: JSON.stringify({ reason }),
  }),
  locations: () => makeRequest('/waste/locations'),
  createLocation: (label) => makeRequest('/waste/locations', { method: 'POST', body: JSON.stringify({ label }) }),
  deactivateLocation: (id) => makeRequest(`/waste/locations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  components: (search) => makeRequest(`/waste/components${search ? `?search=${encodeURIComponent(search)}` : ''}`),
  createComponent: (payload) => makeRequest('/waste/components', { method: 'POST', body: JSON.stringify(payload) }),
  updateComponent: (id, payload) => makeRequest(`/waste/components/${encodeURIComponent(id)}`, {
    method: 'PATCH', body: JSON.stringify(payload),
  }),
};

// Public customer delivery tracking page (/track/:token) — no login.
export const trackingApi = {
  get: (token) => makeRequest(`/track/${encodeURIComponent(token)}`),
  // Static image of the delivery address, rendered server-side (no key in the browser).
  mapUrl: (token) => `${BASE_URL}${API_PREFIX}/track/${encodeURIComponent(token)}/map`,
  // The short review link (track.twigs.net/r/<code>) forwards here: the API logs the click and redirects.
  reviewShortRedirect: (code) => `${BASE_URL}${API_PREFIX}/r/${encodeURIComponent(code)}`,
  // Review buttons go through the API so the click is logged (and no review text follows).
  reviewUrl: (token, to) => `${BASE_URL}${API_PREFIX}/track/${encodeURIComponent(token)}/review?to=${to}`,
  // Problem report from the "Need help?" card. FormData: category, body, contactPhone, photo.
  reportIssue: async (token, formData) => {
    const res = await fetch(`${BASE_URL}${API_PREFIX}/track/${encodeURIComponent(token)}/issue`, { method: 'POST', body: formData });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.message || 'We could not send that. Please call us instead.');
    return data;
  },
};

// Problem reports from the tracking page (staff side)
export const issuesApi = {
  list: ({ status = 'open', orderId } = {}) => {
    const params = new URLSearchParams({ status });
    if (orderId) params.append('orderId', orderId);
    return makeRequest(`/issues?${params.toString()}`);
  },
  resolve: (id, note) => makeRequest(`/issues/${encodeURIComponent(id)}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  }),
};

export const weeklyScanApi = {
  // `today` (store-local) makes a week that contains it a week-to-date comparison.
  get: (weekStart, { today, includeEvents } = {}) =>
    makeRequest(`/weekly-scan?${new URLSearchParams({ weekStart, ...(today ? { today } : {}), ...(includeEvents ? { includeEvents: '1' } : {}) })}`),
  // The orders behind one figure: segment is all | web | inStore | events | seller:<name>.
  orders: ({ start, end, segment = 'all', includeEvents }) =>
    makeRequest(`/weekly-scan/orders?${new URLSearchParams({ start, end, segment, ...(includeEvents ? { includeEvents: '1' } : {}) })}`),
};

export const bookingPacketApi = {
  get: (token) => makeRequest(`/booking-packet/public/${encodeURIComponent(token)}`),
  sign: (token, signerName) => makeRequest(`/booking-packet/public/${encodeURIComponent(token)}/sign`, {
    method: 'POST', body: JSON.stringify({ signerName }),
  }),
};

export const visionQuestionnaireApi = {
  get: (token) => makeRequest(`/vision-questionnaire/public/${encodeURIComponent(token)}`),
  submit: (token, payload) => makeRequest(`/vision-questionnaire/public/${encodeURIComponent(token)}`, {
    method: 'POST', body: JSON.stringify(payload),
  }),
  palette: (token, imageUrl) => makeRequest(`/vision-questionnaire/public/${encodeURIComponent(token)}/palette`, {
    method: 'POST', body: JSON.stringify({ imageUrl }),
  }),
  uploadImage: async (token, file) => {
    const fd = new FormData();
    fd.append('image', file);
    const res = await fetch(`${BASE_URL}${API_PREFIX}/vision-questionnaire/public/${encodeURIComponent(token)}/images`, { method: 'POST', body: fd });
    if (!res.ok) throw new ApiError(`Upload failed: HTTP ${res.status}`, res.status);
    return res.json();
  },
};

// Mother's Day 2026 gift campaign — config + gift product info for the
// real-time auto-add preview in OrderForm.
export const mdGiftApi = {
  getConfig: () => makeRequest('/md-gift-config', { method: 'GET' }),
};

// Customers API
export const customersApi = {
  // Get all customers
  getAll: () => makeRequest('/customers'),

  // Search customers
  search: (query) => makeRequest(`/customers/search?q=${encodeURIComponent(query)}`),

  // Get recent customers
  getRecent: () => makeRequest('/customers/recent'),

  // Get customer by ID
  getById: (id) => makeRequest(`/customers/${id}`),

  // Create new customer
  create: (customerData) => makeRequest('/customers/create', {
    method: 'POST',
    body: JSON.stringify(customerData),
  }),

  // Update customer
  update: (id, updateData) => makeRequest(`/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  }),

  // Delete customer
  delete: (id) => makeRequest(`/customers/${id}`, {
    method: 'DELETE',
  }),

  // Get previous recipients for a customer
  getPreviousRecipients: (customerId) => makeRequest(`/customers/${customerId}/recipients`),
};

// Products API
export const productsApi = {
  // Get all products with search/filter
  getAll: (options = {}) => {
    const {
      search,
      status,
      product_type,
      is_shopify_synced,
      online,
      collection,
      limit = 100,
      offset = 0
    } = options;

    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (product_type) params.append('product_type', product_type);
    if (is_shopify_synced !== undefined) params.append('is_shopify_synced', is_shopify_synced);
    if (online !== undefined && online !== '') params.append('online', online);
    if (collection) params.append('collection', collection);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    return makeRequest(`/products?${params}`);
  },

  // Get product by ID
  getById: (id) => makeRequest(`/products/${id}`),

  // Get product variants
  getVariants: (productId) => makeRequest(`/products/${productId}/variants`),

  // Live stock for variants, read from Shopify (tickets: seats left per slot)
  getVariantInventory: (variantIds) =>
    makeRequest(`/products/variant-inventory?ids=${encodeURIComponent(variantIds.join(','))}`),

  // Create new product
  create: (productData) => makeRequest('/products', {
    method: 'POST',
    body: JSON.stringify(productData),
  }),

  // Update product
  update: (id, updateData) => makeRequest(`/products/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updateData),
  }),

  // Delete product
  delete: (id) => makeRequest(`/products/${id}`, {
    method: 'DELETE',
  }),

  // Bulk delete products
  bulkDelete: (options) => makeRequest('/products/bulk-delete', {
    method: 'POST',
    body: JSON.stringify(options),
  }),

  // Sync products from Shopify
  syncFromShopify: (options = {}) => makeRequest('/products/sync/shopify', {
    method: 'POST',
    body: JSON.stringify(options),
  }),

  // Autocomplete search for products
  autocomplete: (query, limit = 10) => makeRequest(`/products/autocomplete?q=${encodeURIComponent(query)}&limit=${limit}`),

  // Get placeholder product mapping for custom item product type tracking
  getPlaceholders: () => makeRequest('/products/placeholders'),

  // Online store (Phase 1): which add-ons a product offers
  setAddons: (id, addonIds) => makeRequest(`/products/${id}/addons`, {
    method: 'PUT',
    body: JSON.stringify({ addon_ids: addonIds }),
  }),
  // Gallery: upload (multipart, see uploadProductImage), reorder, delete
  reorderImages: (id, imageIds) => makeRequest(`/products/${id}/images/order`, {
    method: 'PUT',
    body: JSON.stringify({ image_ids: imageIds }),
  }),
  deleteImage: (id, imageId) => makeRequest(`/products/${id}/images/${imageId}`, { method: 'DELETE' }),
};

// FormData upload for a product gallery image — cannot use makeRequest (JSON Content-Type).
export async function uploadProductImage(productId, file, alt) {
  const token = await ensureValidToken();
  const fd = new FormData();
  fd.append('image', file);
  if (alt) fd.append('alt', alt);
  const res = await fetch(`${BASE_URL}${API_PREFIX}/products/${encodeURIComponent(productId)}/images`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(body.message || `Upload failed: HTTP ${res.status}`, res.status);
  return body;
}

// Online store: collections
export const collectionsApi = {
  getAll: (options = {}) => {
    const params = new URLSearchParams();
    if (options.published !== undefined) params.append('published', options.published);
    if (options.kind) params.append('kind', options.kind);
    const qs = params.toString();
    return makeRequest(`/collections${qs ? `?${qs}` : ''}`);
  },
  getById: (id) => makeRequest(`/collections/${id}`),
  create: (data) => makeRequest('/collections', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => makeRequest(`/collections/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => makeRequest(`/collections/${id}`, { method: 'DELETE' }),
  setProducts: (id, productIds) => makeRequest(`/collections/${id}/products`, {
    method: 'PUT',
    body: JSON.stringify({ product_ids: productIds }),
  }),
  setAddons: (id, addonIds) => makeRequest(`/collections/${id}/addons`, {
    method: 'PUT',
    body: JSON.stringify({ addon_ids: addonIds }),
  }),
  rematerialise: (id) => makeRequest(`/collections/${id}/rematerialise`, { method: 'POST' }),
};

// Online store: add-ons
// Online store Phase 8A: catalog import (Hana crawl or saved files, CSV, WooCommerce).
export const catalogImportApi = {
  list: () => makeRequest('/catalog-import/jobs'),
  get: (id) => makeRequest(`/catalog-import/jobs/${id}`),
  create: (data) => makeRequest('/catalog-import/jobs', { method: 'POST', body: JSON.stringify(data) }),
  setMapping: (id, mapping) => makeRequest(`/catalog-import/jobs/${id}/mapping`, { method: 'PUT', body: JSON.stringify({ mapping }) }),
  // hanaFee: { acceptHanaFee: true, hanaFeeCode? } after the Hana import fee confirm (409 HANA_IMPORT_FEE_CONFIRM).
  start: (id, hanaFee = {}) => makeRequest(`/catalog-import/jobs/${id}/import`, { method: 'POST', body: JSON.stringify({ acknowledge_images: true, ...hanaFee }) }),
  cancel: (id) => makeRequest(`/catalog-import/jobs/${id}/cancel`, { method: 'POST' }),
};

// Online store Phase 8B: old-site URL redirects.
export const redirectsApi = {
  list: (options = {}) => {
    const params = new URLSearchParams();
    if (options.q) params.append('q', options.q);
    if (options.status) params.append('status', options.status);
    const qs = params.toString();
    return makeRequest(`/redirects${qs ? `?${qs}` : ''}`);
  },
  test: (path) => makeRequest(`/redirects/test?path=${encodeURIComponent(path)}`),
  create: (data) => makeRequest('/redirects', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => makeRequest(`/redirects/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => makeRequest(`/redirects/${id}`, { method: 'DELETE' }),
  approve: (data) => makeRequest('/redirects/approve', { method: 'POST', body: JSON.stringify(data) }),
  setGroup: (key, data) => makeRequest(`/redirects/groups/${key}`, { method: 'PUT', body: JSON.stringify(data) }),
  propose: (jobId) => makeRequest('/redirects/propose', { method: 'POST', body: JSON.stringify(jobId ? { job_id: jobId } : {}) }),
  importCsv: (csv) => makeRequest('/redirects/import-csv', { method: 'POST', body: JSON.stringify({ csv }) }),
};

// Online store → Domain (Phase 6B, owner only): custom domains with DNS and
// certificate status. Backend: routes/domains.js. `id` 'subdomain' in
// makePrimary makes the shop's own subdomain the primary address again.
export const domainsApi = {
  list: () => makeRequest('/domains'),
  add: (domain) => makeRequest('/domains', { method: 'POST', body: JSON.stringify({ domain }) }),
  check: (id) => makeRequest(`/domains/${id}/check`, { method: 'POST' }),
  makePrimary: (id) => makeRequest(`/domains/${id}/primary`, { method: 'POST' }),
  remove: (id) => makeRequest(`/domains/${id}`, { method: 'DELETE' }),
  domainConnect: (id) => makeRequest(`/domains/${id}/domain-connect`),
};

// Online store Phase 6C: the shop's own sending domain (owner only).
export const emailDomainApi = {
  get: () => makeRequest('/email-domain'),
  setSendFrom: (address) => makeRequest('/email-domain/send-from', { method: 'PUT', body: JSON.stringify({ address }) }),
  check: () => makeRequest('/email-domain/check', { method: 'POST' }),
  remove: () => makeRequest('/email-domain', { method: 'DELETE' }),
  domainConnect: () => makeRequest('/email-domain/domain-connect'),
};

// Online store Phase 4C: discount codes (writes are admin/owner-only).
export const discountsApi = {
  list: () => makeRequest('/discounts'),
  get: (id) => makeRequest(`/discounts/${encodeURIComponent(id)}`),
  create: (data) => makeRequest('/discounts', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => makeRequest(`/discounts/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => makeRequest(`/discounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

// Online store Phase 4C: gift cards. issue() answers the code once; nothing shows it again.
export const giftCardsApi = {
  list: (q) => makeRequest(`/gift-cards${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  get: (id) => makeRequest(`/gift-cards/${encodeURIComponent(id)}`),
  issue: (data) => makeRequest('/gift-cards', { method: 'POST', body: JSON.stringify(data) }),
  adjust: (id, data) => makeRequest(`/gift-cards/${encodeURIComponent(id)}/adjust`, { method: 'POST', body: JSON.stringify(data) }),
  setDisabled: (id, disabled) => makeRequest(`/gift-cards/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify({ disabled }) }),
  // { source, csv (the file's text), dry_run } — never stored on the server.
  importCards: (data) => makeRequest('/gift-cards/import', { method: 'POST', body: JSON.stringify(data) }),
  exchange: (id) => makeRequest(`/gift-cards/${encodeURIComponent(id)}/exchange`, { method: 'POST' }),
};

// Online store 5A: shopper accounts (read-only; owners and admins may delete one).
export const storeCustomersApi = {
  list: () => makeRequest('/store-customers'),
  remove: (id) => makeRequest(`/store-customers/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  // Online store 9A: subscriptions sold on the store (read-only list).
  subscriptions: () => makeRequest('/store-customers/subscriptions'),
  // Online store 5C: newsletter signups (owner/admin).
  newsletter: () => makeRequest('/store-customers/newsletter'),
};

// Online store 5C: the newsletter list as CSV (owner/admin; stamps exported_at) — returns a Blob.
export async function downloadNewsletterCsv() {
  const res = await fetch(`${BASE_URL}${API_PREFIX}/store-customers/newsletter/export`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new ApiError(`CSV failed: HTTP ${res.status}`, res.status);
  return res.blob();
}

// Online store 5C: the storefront announcement bar (times shop-local 'YYYY-MM-DDTHH:MM').
export const storeAnnouncementApi = {
  get: () => makeRequest('/store/announcement'),
  save: (data) => makeRequest('/store/announcement', { method: 'PUT', body: JSON.stringify(data) }),
  clear: () => makeRequest('/store/announcement', { method: 'DELETE' }),
};

// Online store 3C: Theme (colors, fonts, logo/favicon, home sections) and its preview token.
export const storeThemeApi = {
  get: () => makeRequest('/store/theme'),
  save: (data) => makeRequest('/store/theme', { method: 'PUT', body: JSON.stringify(data) }),
  preview: (data) => makeRequest('/storefront/preview', { method: 'POST', body: JSON.stringify(data) }),
};

// FormData upload for the theme's logo or favicon — cannot use makeRequest (JSON Content-Type).
export async function uploadThemeImage(kind, file) {
  const token = await ensureValidToken();
  const fd = new FormData();
  fd.append('image', file);
  const res = await fetch(`${BASE_URL}${API_PREFIX}/store/theme/upload?kind=${encodeURIComponent(kind)}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(body.message || `Upload failed: HTTP ${res.status}`, res.status, body);
  return body;
}

// Online store 3C: Pages (rich-text JSON bodies) and the header/footer Menus.
export const storePagesApi = {
  list: () => makeRequest('/store/pages'),
  get: (id) => makeRequest(`/store/pages/${encodeURIComponent(id)}`),
  create: (data) => makeRequest('/store/pages', { method: 'POST', body: JSON.stringify(data) }),
  save: (id, data) => makeRequest(`/store/pages/${encodeURIComponent(id)}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id) => makeRequest(`/store/pages/${encodeURIComponent(id)}`, { method: 'DELETE' }),
};

export const storeMenusApi = {
  get: () => makeRequest('/store/menus'),
  save: (handle, items) => makeRequest(`/store/menus/${encodeURIComponent(handle)}`, { method: 'PUT', body: JSON.stringify({ items }) }),
};

// FormData upload for an image inside a page body; resolves to the image URL.
export async function uploadPageImage(file) {
  const token = await ensureValidToken();
  const fd = new FormData();
  fd.append('image', file);
  const res = await fetch(`${BASE_URL}${API_PREFIX}/store/pages/upload`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(body.message || `Upload failed: HTTP ${res.status}`, res.status, body);
  return body.data.url;
}

// Online store Phase 7E: Inbound orders (WooCommerce connection, Needs attention, event log).
export const inboundApi = {
  presets: () => makeRequest('/inbound/presets'),
  getIntegration: (platform) => makeRequest(`/inbound/integrations/${platform}`),
  connect: (platform, data) => makeRequest(`/inbound/integrations/${platform}`, { method: 'POST', body: JSON.stringify(data) }),
  update: (platform, data) => makeRequest(`/inbound/integrations/${platform}`, { method: 'PUT', body: JSON.stringify(data) }),
  revealSecret: (platform) => makeRequest(`/inbound/integrations/${platform}/secret`),
  test: (platform) => makeRequest(`/inbound/integrations/${platform}/test`, { method: 'POST' }),
  disconnect: (platform) => makeRequest(`/inbound/integrations/${platform}/disconnect`, { method: 'POST' }),
  squarespaceOAuthStart: () => makeRequest('/inbound/integrations/squarespace/oauth/start', { method: 'POST' }),
  heldOrders: () => makeRequest('/orders?limit=100&page=1&intake_status=needs_attention'),
  needsAttention: () => makeRequest('/inbound/needs-attention'),
  pairProduct: (data) => makeRequest('/inbound/product-map', { method: 'PUT', body: JSON.stringify(data) }),
  resolve: (orderId) => makeRequest(`/orders/${encodeURIComponent(orderId)}/resolve-intake`, { method: 'PUT' }),
  events: (limit = 100) => makeRequest(`/inbound/events?limit=${limit}`),
  // Wix (7C): connected by installing the app, not by a form.
  wix: () => makeRequest('/inbound/integrations/wix'),
  wixInstallLink: () => makeRequest('/inbound/integrations/wix/install-link', { method: 'POST' }),
};

// Online store Phase 8D: the "Switching from Hana" checklist, every step from real state.
// Online store 5B: abandoned-cart emails sent and carts recovered (last 30 days).
export const storeAbandonedCartsApi = {
  get: () => makeRequest('/store/abandoned-carts'),
};

// GEO (2026-11-03): can the AI crawlers reach the store, and what AI traffic it has seen.
export const storeAiVisibilityApi = {
  get: () => makeRequest('/store/ai-visibility'),
  check: () => makeRequest('/store/ai-visibility/check', { method: 'POST', body: JSON.stringify({}) }),
};

export const storeGoLiveApi = {
  get: () => makeRequest('/store/go-live'),
  publish: (body = {}) => makeRequest('/store/go-live/publish', { method: 'POST', body: JSON.stringify(body) }),
  unpublish: () => makeRequest('/store/go-live/unpublish', { method: 'POST', body: JSON.stringify({}) }),
};

export const addonsApi = {
  getAll: (options = {}) => {
    const params = new URLSearchParams();
    if (options.active !== undefined) params.append('active', options.active);
    const qs = params.toString();
    return makeRequest(`/addons${qs ? `?${qs}` : ''}`);
  },
  getById: (id) => makeRequest(`/addons/${id}`),
  forProduct: (productId) => makeRequest(`/addons/for-product/${productId}`),
  create: (data) => makeRequest('/addons', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => makeRequest(`/addons/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => makeRequest(`/addons/${id}`, { method: 'DELETE' }),
};

// Health check
export const healthApi = {
  check: () => makeRequest('/health'),
};

// Delivery zones and rules (Phase 2): the order form quotes through the server.
export const deliveryApi = {
  // { lat, lng } for distance shops, { zip } for zip shops
  quote: (params) => makeRequest(`/delivery/quote?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')))}`),
  rules: () => makeRequest('/delivery/rules'),
  saveZones: (zones) => makeRequest('/delivery/zones', { method: 'PUT', body: JSON.stringify({ zones }) }),
  getCapacity: () => makeRequest('/delivery/capacity'),
  saveCapacity: (capacity) => makeRequest('/delivery/capacity', { method: 'PUT', body: JSON.stringify({ capacity }) }),
};

// Delivery Routes API functions
export const deliveryRoutesApi = {
  // Get all routes with optional filtering
  getAll: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return makeRequest(`/delivery-routes${queryString ? '?' + queryString : ''}`);
  },

  // Get specific route with stops
  // Add cache buster to prevent 304 responses after updates
  getById: (id) => makeRequest(`/delivery-routes/${id}?_=${Date.now()}`),

  // Create new route
  create: (routeData) => makeRequest('/delivery-routes', {
    method: 'POST',
    body: JSON.stringify(routeData)
  }),

  // Update route
  update: (id, updates) => makeRequest(`/delivery-routes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),

  // Delete route
  delete: (id) => makeRequest(`/delivery-routes/${id}`, {
    method: 'DELETE'
  }),

  // Add orders to route
  addStops: (routeId, orderIds) => makeRequest(`/delivery-routes/${routeId}/stops`, {
    method: 'POST',
    body: JSON.stringify({ order_ids: orderIds })
  }),

  // Reorder stops in route
  reorderStops: (routeId, stops) => makeRequest(`/delivery-routes/${routeId}/stops/reorder`, {
    method: 'PUT',
    body: JSON.stringify({ stops })
  }),

  // Update individual stop
  updateStop: (stopId, updates) => makeRequest(`/delivery-routes/stops/${stopId}`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),

  // Remove stop from route
  removeStop: (stopId) => makeRequest(`/delivery-routes/stops/${stopId}`, {
    method: 'DELETE'
  }),

  // Move stop between routes
  moveStopBetweenRoutes: (stopId, toRouteId) => makeRequest(`/delivery-routes/stops/${stopId}/move`, {
    method: 'PUT',
    body: JSON.stringify({ route_id: toRouteId })
  }),

  // Get unassigned orders
  getUnassignedOrders: (date) => {
    const params = date ? `?date=${date}` : '';
    return makeRequest(`/delivery-routes/orders/unassigned${params}`);
  },

  // Get delivery summary for date
  getSummary: (date) => makeRequest(`/delivery-routes/summary/${date}`),

  // Get active drivers
  getActiveDrivers: () => makeRequest('/delivery-routes/drivers/active'),

  // Calculate route metrics (distance, duration) - handled client-side due to API key restrictions
  calculateMetrics: async (routeId, stops, options = {}) => {
    // This is now handled client-side in TripBuilderOverlay to use browser API key
    throw new Error('Use client-side calculation instead');
  },

  // Dispatch route to driver (mark as dispatched, send push notification)
  dispatchRoute: (routeId) => makeRequest(`/delivery-routes/${routeId}/dispatch`, {
    method: 'POST'
  }),

  // Calculate ETA for remaining stops (leg estimate anchored on the last real
  // event; a foreground GPS fix refines it when the driver has the app open)
  calculateETA: (routeId) => makeRequest(`/delivery-routes/${routeId}/eta`, {
    method: 'POST'
  }),

  // Estimated position + ETAs for every in-progress route (dispatch map)
  getActiveProgress: (date) => makeRequest(`/delivery-routes/progress/active${date ? `?date=${encodeURIComponent(date)}` : ''}`),

  // Start a route (mark as in_progress)
  startRoute: (routeId) => makeRequest(`/delivery-routes/${routeId}/start`, {
    method: 'POST'
  }),

  // Confirm delivery for a stop
  confirmDelivery: (stopId, deliveryData) => makeRequest(`/delivery-routes/stops/${stopId}/confirm-delivery`, {
    method: 'POST',
    body: JSON.stringify(deliveryData)
  }),

  // Mark stop as not delivered (revert to pending)
  undeliverStop: (stopId) => makeRequest(`/delivery-routes/stops/${stopId}/undeliver`, {
    method: 'PUT'
  }),

  // Update delivery details (time, method)
  updateDelivery: (stopId, updates) => makeRequest(`/delivery-routes/stops/${stopId}/update-delivery`, {
    method: 'PUT',
    body: JSON.stringify(updates)
  }),

  // Generate route suggestions for a date
  suggestRoutes: (date) => makeRequest('/delivery-routes/suggest', {
    method: 'POST',
    body: JSON.stringify({ date })
  }),

  // Get pending suggestions for a date
  getSuggestions: (date) => makeRequest(`/delivery-routes/suggestions/${date}`),

  // Approve suggestions and create routes
  approveSuggestions: (suggestionId, data) => makeRequest(`/delivery-routes/suggestions/${suggestionId}/approve`, {
    method: 'POST',
    body: JSON.stringify(data)
  }),

  // Reject suggestions
  rejectSuggestions: (suggestionId) => makeRequest(`/delivery-routes/suggestions/${suggestionId}/reject`, {
    method: 'POST',
    body: JSON.stringify({})
  }),

  // Optimize stop sequence within an existing route
  optimizeSequence: (routeId) => makeRequest(`/delivery-routes/${routeId}/optimize-sequence`, {
    method: 'POST'
  }),

  // Find best insertion point for a new order in an existing route
  findInsertion: (routeId, orderId) => makeRequest(`/delivery-routes/${routeId}/find-insertion`, {
    method: 'POST',
    body: JSON.stringify({ order_id: orderId })
  })
};

// Generic API function for custom requests
export const apiRequest = makeRequest;

// Export the ApiError class for error handling in components
export { ApiError };

// Utility function to handle API errors in components
export const handleApiError = (error, fallbackMessage = 'An error occurred') => {
  if (error instanceof ApiError) {
    return {
      message: error.message,
      status: error.status,
      data: error.data,
    };
  }

  return {
    message: fallbackMessage,
    status: 0,
    data: null,
  };
};

// Auth API functions
export const authApi = {
  login: async (credentials, password) => {
    // Support both old format (email, password) and new format (credentials object)
    const loginData = typeof credentials === 'string'
      ? { email: credentials, password }
      : credentials;

    return apiRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(loginData)
    });
  },

  // The shop picked when a login works at more than one shop.
  chooseLoginShop: async (selectionToken, tenantId) => {
    return apiRequest('/auth/login/choose', {
      method: 'POST',
      body: JSON.stringify({ selectionToken, tenantId })
    });
  },

  signup: async (userData) => {
    return apiRequest('/auth/signup', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  },

  getProfile: async () => {
    return apiRequest('/auth/profile');
  },

  logout: async () => {
    return apiRequest('/auth/logout', {
      method: 'POST'
    });
  },

  updateProfile: async (userData) => {
    return apiRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(userData)
    });
  },

  changePassword: async (currentPassword, newPassword) => {
    return apiRequest('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  },

  // Refresh token - get a new token before the current one expires
  refreshToken: async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No token to refresh');
    }

    const response = await fetch(`${BASE_URL}${API_PREFIX}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Token refresh failed');
    }

    const data = await response.json();
    if (data.success && data.data?.token) {
      localStorage.setItem('token', data.data.token);
      console.log('[Auth] Token refreshed successfully');
      return data.data.token;
    }

    throw new Error('Invalid refresh response');
  },

  forgotPassword: async (email) => {
    return apiRequest('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  },

  validateResetToken: async (token) => {
    return apiRequest(`/auth/validate-reset-token/${token}`);
  },

  resetPassword: async (token, newPassword) => {
    return apiRequest('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword })
    });
  },

  // Admin user management functions
  admin: {
    getAllUsers: async () => {
      return apiRequest('/auth/admin/users');
    },

    getPendingUsers: async () => {
      return apiRequest('/auth/admin/users/pending');
    },

    createUser: async (userData) => {
      return apiRequest('/auth/admin/users', {
        method: 'POST',
        body: JSON.stringify(userData)
      });
    },

    updateUser: async (userId, userData) => {
      return apiRequest(`/auth/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify(userData)
      });
    },

    deleteUser: async (userId) => {
      return apiRequest(`/auth/admin/users/${userId}`, {
        method: 'DELETE'
      });
    },

    approveUser: async (userId) => {
      return apiRequest(`/auth/admin/users/${userId}/approve`, {
        method: 'PUT'
      });
    },

    rejectUser: async (userId) => {
      return apiRequest(`/auth/admin/users/${userId}/reject`, {
        method: 'PUT'
      });
    },

    getActiveDrivers: async () => {
      return apiRequest('/auth/admin/drivers/active');
    },

    updateDriverStatus: async (userId, driverStatus) => {
      return apiRequest(`/auth/admin/users/${userId}/driver-status`, {
        method: 'PUT',
        body: JSON.stringify({ driverStatus })
      });
    }
  }
};

// Search API
export const searchApi = {
  // Instant search for autocomplete
  instant: async (query, limit = 5) => {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString()
    });
    return apiRequest(`/search/instant?${params}`);
  },
  
  // Full search with pagination and filters
  search: async (query, options = {}) => {
    const {
      page = 1,
      limit = 20,
      status,
      dateFrom,
      dateTo
    } = options;
    
    const params = new URLSearchParams({
      q: query,
      page: page.toString(),
      limit: limit.toString()
    });
    
    if (status) params.append('status', status);
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);
    
    return apiRequest(`/search?${params}`);
  },
  
  // Get search suggestions
  suggestions: async (query) => {
    const params = new URLSearchParams({ q: query });
    return apiRequest(`/search/suggestions?${params}`);
  }
};

// Utility function to format error messages for user display
export const getErrorMessage = (error) => {
  if (error instanceof ApiError) {
    // Prefer the server-provided message when it's specific.
    // Many backend routes return a status >= 500 with a useful message
    // (e.g. "Failed to create order: Order validation failed: ...").
    // Falling back to a generic "Server error" in that case strips the
    // detail staff need to act on the failure.
    const serverMessage = error.message;
    const looksGeneric = !serverMessage ||
      /^HTTP \d+:/i.test(serverMessage) ||
      serverMessage === error.data?.error ||
      serverMessage.length < 5;

    if (error.status === 404) {
      return looksGeneric ? 'The requested resource was not found.' : serverMessage;
    } else if (error.status === 403) {
      return looksGeneric ? 'You do not have permission to perform this action.' : serverMessage;
    } else if (error.status === 401) {
      return looksGeneric ? 'Authentication required. Please check your credentials.' : serverMessage;
    } else if (error.status >= 500) {
      return looksGeneric ? 'Server error. Please try again later.' : serverMessage;
    } else if (error.status === 0) {
      return 'Network error. Please check your connection and try again.';
    }

    return serverMessage || 'An error occurred';
  }

  return error.message || 'An unexpected error occurred';
};

// Drafts API (Server-side persistence for multi-user access)
export const draftsApi = {
  // Get all drafts with optional filtering
  getAll: (options = {}) => {
    const { customer_search, created_by, limit = 50, offset = 0, my_drafts_only } = options;
    const params = new URLSearchParams();

    if (customer_search) params.append('customer_search', customer_search);
    if (created_by) params.append('created_by', created_by);
    if (my_drafts_only) params.append('my_drafts_only', 'true');
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());

    return makeRequest(`/drafts?${params}`);
  },

  // Get drafts for specific customer
  getByCustomer: (customer) => {
    const params = new URLSearchParams();
    if (customer.phone) params.append('phone', customer.phone);
    if (customer.email) params.append('email', customer.email);
    if (customer.name) params.append('name', customer.name);

    return makeRequest(`/drafts/customer?${params}`);
  },

  // Get single draft by ID
  getById: (id) => makeRequest(`/drafts/${id}`),

  // Create new draft
  create: (formData, notes = null) => makeRequest('/drafts', {
    method: 'POST',
    body: JSON.stringify({ form_data: formData, notes }),
  }),

  // Update existing draft
  update: (id, formData, notes = null) => makeRequest('/drafts', {
    method: 'POST',
    body: JSON.stringify({ id, form_data: formData, notes }),
  }),

  // Update draft metadata only (notes, etc.)
  updateMetadata: (id, updates) => makeRequest(`/drafts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(updates),
  }),

  // Delete single draft
  delete: (id) => makeRequest(`/drafts/${id}`, {
    method: 'DELETE',
  }),

  // Delete multiple drafts
  deleteBulk: (draftIds) => makeRequest('/drafts', {
    method: 'DELETE',
    body: JSON.stringify({ draft_ids: draftIds }),
  }),

  // Delete all my drafts
  deleteAllMine: () => makeRequest('/drafts', {
    method: 'DELETE',
    body: JSON.stringify({ delete_all_mine: true }),
  }),

  // Send a draft invoice/estimate email to the customer
  sendDraftInvoiceEmail: (formData, recipientEmail, totals) => makeRequest('/drafts/send-invoice-email', {
    method: 'POST',
    body: JSON.stringify({ formData, recipientEmail, totals }),
  }),

  // Preview the draft invoice HTML for staff review before sending
  previewDraftInvoiceEmail: (formData, totals) => makeRequest('/drafts/preview-invoice-email', {
    method: 'POST',
    body: JSON.stringify({ formData, totals }),
  }),
};

// Work Tickets API
export const workTicketsApi = {
  // Print work ticket via connected print client
  print: (orderId) => makeRequest(`/work-tickets/print/${orderId}`, {
    method: 'POST',
  }),

  // Retry printing a work ticket (logs as 'retry')
  retryPrint: (orderId) => makeRequest(`/work-tickets/retry-print/${orderId}`, {
    method: 'POST',
  }),

  // Get print logs for an order
  getPrintLogs: (orderId) => makeRequest(`/work-tickets/print-logs/${orderId}`),

  // Get paginated list of print logs with filtering
  getPrintLogsList: (options = {}) => {
    const { startDate, endDate, status = 'all', limit = 100, offset = 0 } = options;
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (status !== 'all') params.append('status', status);
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    return makeRequest(`/work-tickets/print-logs?${params}`);
  },

  // Get print status for multiple orders (batch)
  getPrintStatus: (orderIds) => {
    if (!orderIds || orderIds.length === 0) {
      return Promise.resolve({ success: true, statuses: {} });
    }
    return makeRequest(`/work-tickets/print-status?orderIds=${orderIds.join(',')}`);
  },

  // Get PDF download (fallback if no print client)
  download: (orderId) =>
    openAuthenticatedFile(`${BASE_URL}${API_PREFIX}/work-tickets/download/${orderId}`)
      .catch((error) => console.error('Work ticket download failed:', error)),

  // Check print client connection status
  getStatus: () => makeRequest('/work-tickets/print-clients/status'),

  // Preview HTML (for debugging)
  preview: (orderId) =>
    openAuthenticatedFile(`${BASE_URL}${API_PREFIX}/work-tickets/preview/${orderId}`)
      .catch((error) => console.error('Work ticket preview failed:', error)),

  // Generate a draft work ticket PDF from raw form data (opens in new tab)
  generateFromDraft: async (formData) => {
    const url = `${BASE_URL}${API_PREFIX}/work-tickets/generate-from-draft`;
    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(formData),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Failed to generate draft work ticket' }));
      throw new Error(err.message || 'Failed to generate draft work ticket');
    }
    const blob = await response.blob();
    const pdfUrl = URL.createObjectURL(blob);
    window.open(pdfUrl, '_blank');
  },

  // Route a draft work ticket to the connected print client (+ print logging),
  // like auto-print. Throws on 503 when no client is connected so the caller can
  // fall back to generateFromDraft (open + print manually).
  printDraft: (formData) => makeRequest('/work-tickets/print-draft', {
    method: 'POST',
    body: JSON.stringify(formData),
  }),
};

// This shop's print client computers (paired by signing in on the client) and
// the single shop key: issued once, then only its last four show.
export const printClientApi = {
  getDevices: () => makeRequest('/print-client/devices'),
  unpairDevice: (deviceId) => makeRequest(`/print-client/pair/${encodeURIComponent(deviceId)}`, { method: 'DELETE' }),
  getKey: () => makeRequest('/print-client/key'),
  issueKey: () => makeRequest('/print-client/key', { method: 'POST' }),
  revokeKey: () => makeRequest('/print-client/key', { method: 'DELETE' }),
  // What the print client's "Server URL" field takes (it adds the socket path).
  serverUrl: `${BASE_URL}${API_PREFIX}`,
};

// Staff Members API
export const staffMembersApi = {
  getAll: () => makeRequest('/staff-members'),
  create: (data) => makeRequest('/staff-members', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => makeRequest(`/staff-members/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => makeRequest(`/staff-members/${id}`, { method: 'DELETE' }),
  updateOrder: (orderData) => makeRequest('/staff-members/reorder', { method: 'POST', body: JSON.stringify({ order: orderData }) }),
};

// Drivers API
export const driversApi = {
  getAll: () => makeRequest('/drivers'),
  create: (data) => makeRequest('/drivers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => makeRequest(`/drivers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => makeRequest(`/drivers/${id}`, { method: 'DELETE' }),
  resetPassword: (id) => makeRequest(`/drivers/${id}/reset-password`, { method: 'POST' }),
  updateOrder: (orderData) => makeRequest('/drivers/reorder', { method: 'POST', body: JSON.stringify({ order: orderData }) }),
  getLatestLocations: () => makeRequest('/drivers/locations/latest'),
};

// Customers DB API (local database - customer management page)
export const customersDbApi = {
  // The shop's internal customers (one per purpose), for the Internal order name suggestions
  listInternal: () => makeRequest('/customers-db/internal'),

  // House account contacts: the people who place orders for the company.
  // Active only unless { all: true }.
  listContacts: (customerId, { all } = {}) =>
    makeRequest(`/customers-db/${customerId}/contacts${all ? '?all=1' : ''}`),
  createContact: (customerId, data) => makeRequest(`/customers-db/${customerId}/contacts`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  updateContact: (contactId, data) => makeRequest(`/customers-db/contacts/${contactId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  deleteContact: (contactId) => makeRequest(`/customers-db/contacts/${contactId}`, { method: 'DELETE' }),

  // List/search customers with pagination
  getAll: (options = {}) => {
    const { q, page = 1, limit = 25, house_accounts_only, sort, sort_dir } = options;
    const params = new URLSearchParams();
    if (q) params.append('q', q);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (house_accounts_only) params.append('house_accounts_only', 'true');
    if (sort) params.append('sort', sort);
    if (sort_dir) params.append('sort_dir', sort_dir);
    return makeRequest(`/customers-db?${params}`);
  },

  // Create house account customer
  create: (data) => makeRequest('/customers-db', {
    method: 'POST',
    body: JSON.stringify(data),
  }),

  // Get customer detail
  getById: (id) => makeRequest(`/customers-db/${id}`),

  // The customer's latest answer to order-update texts: { consented: true | false | null }
  getSmsConsent: (phone) => makeRequest(`/customers-db/sms-consent?phone=${encodeURIComponent(phone)}`),

  // Order-form customer lookup in the local database, for shops without
  // Shopify. Same result shape as customersApi.search (which asks Shopify).
  search: (query) => makeRequest(`/customers-db/search?q=${encodeURIComponent(query)}`),

  // Update customer
  update: (id, data) => makeRequest(`/customers-db/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // Move an email off the customer currently holding it and onto this one
  claimEmail: (id, email) => makeRequest(`/customers-db/${id}/claim-email`, {
    method: 'PUT',
    body: JSON.stringify({ email }),
  }),

  // Delete customer
  delete: (id) => makeRequest(`/customers-db/${id}`, { method: 'DELETE' }),

  // Get customer orders
  getOrders: (id, options = {}) => {
    const { page = 1, limit = 20 } = options;
    return makeRequest(`/customers-db/${id}/orders?page=${page}&limit=${limit}`);
  },

  // Notes
  getNotes: (id) => makeRequest(`/customers-db/${id}/notes`),
  addNote: (id, noteText) => makeRequest(`/customers-db/${id}/notes`, {
    method: 'POST',
    body: JSON.stringify({ noteText }),
  }),
  updateNote: (noteId, noteText) => makeRequest(`/customers-db/notes/${noteId}`, {
    method: 'PUT',
    body: JSON.stringify({ noteText }),
  }),
  deleteNote: (noteId) => makeRequest(`/customers-db/notes/${noteId}`, {
    method: 'DELETE',
  }),

  // Tax exempt status (any customer)
  updateTaxExempt: (id, taxExempt) => makeRequest(`/customers-db/${id}/tax-exempt`, {
    method: 'PUT',
    body: JSON.stringify({ taxExempt }),
  }),

  // Clear "Email bounced" once the address is fixed (customer emails resume).
  clearEmailBounce: (id) => makeRequest(`/customers-db/${id}/email-bounce/clear`, { method: 'PUT' }),

  // House account management
  updateHouseAccount: (id, data) => makeRequest(`/customers-db/${id}/house-account`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // House-account billing details (business name, billing email, billing address)
  updateBillingDetails: (id, data) => makeRequest(`/customers-db/${id}/billing-details`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),

  // Logo upload
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    const token = localStorage.getItem('token');
    return fetch(`${BASE_URL}${API_PREFIX}/customers-db/${id}/logo`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData
    }).then(res => res.json());
  },
  deleteLogo: (id) => makeRequest(`/customers-db/${id}/logo`, { method: 'DELETE' }),
};

// House Account Billing API
export const billingApi = {
  getUnbilled: (customerId, startDate, endDate) => {
    const params = new URLSearchParams({ customerId });
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return makeRequest(`/house-account-billing/unbilled?${params}`);
  },
  generateInvoice: (data) => makeRequest('/house-account-billing/generate-invoice', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  getInvoices: (customerId) => makeRequest(`/house-account-billing/invoices?customerId=${customerId}`),
  getInvoice: (invoiceId) => makeRequest(`/house-account-billing/invoices/${invoiceId}`),
  updateInvoiceStatus: (invoiceId, data) => makeRequest(`/house-account-billing/invoices/${invoiceId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  createShopifyDraft: (invoiceId) => makeRequest(`/house-account-billing/invoices/${invoiceId}/create-shopify-draft`, {
    method: 'POST',
  }),
  markInvoicePaid: (invoiceId, data) => makeRequest(`/house-account-billing/invoices/${invoiceId}/mark-paid`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  getNotifications: () => makeRequest('/house-account-billing/notifications'),
  adjustCredit: (data) => makeRequest('/house-account-billing/credit-adjustment', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  // Emailed invoices for shops without Shopify invoicing.
  getInvoiceSettings: () => makeRequest('/house-account-billing/invoice-settings'),
  updateInvoiceSettings: (data) => makeRequest('/house-account-billing/invoice-settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  emailInvoice: (invoiceId, data = {}) => makeRequest(`/house-account-billing/invoices/${invoiceId}/email`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  openInvoicePdf: (invoiceId) => openAuthenticatedFile(`${BASE_URL}${API_PREFIX}/house-account-billing/invoices/${encodeURIComponent(invoiceId)}/pdf`),
  // Charge the customer's card on file (COF-3): { attemptKey, amount? } (dollars, at most the balance due).
  // A decline answers 402 with errorCode CARD_DECLINED or AUTHENTICATION_REQUIRED and Stripe's reason.
  chargeCardOnFile: (invoiceId, data) => makeRequest(`/house-account-billing/invoices/${invoiceId}/charge-card-on-file`, {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

// Recurring Orders API

// Order-entry layout experiment. Records how long an order took and on which
// layout, so the Classic/Focused choice can be settled with medians rather than
// with opinions. Recording is best-effort and never blocks taking an order.
export const orderEntryMetricsApi = {
  record: ({ layout, durationMs }) =>
    makeRequest('/order-entry-metrics', {
      method: 'POST',
      body: JSON.stringify({ layout, durationMs }),
    }),
  summary: () => makeRequest('/order-entry-metrics/summary'),
};

export const recurringOrdersApi = {
  getAll: (options = {}) => {
    const params = new URLSearchParams();
    if (options.customerId) params.append('customerId', options.customerId);
    if (options.status) params.append('status', options.status);
    return makeRequest(`/recurring-orders?${params}`);
  },
  create: (data) => makeRequest('/recurring-orders', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => makeRequest(`/recurring-orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  pause: (id) => makeRequest(`/recurring-orders/${id}/pause`, { method: 'PUT' }),
  resume: (id) => makeRequest(`/recurring-orders/${id}/resume`, { method: 'PUT' }),
  cancel: (id) => makeRequest(`/recurring-orders/${id}`, { method: 'DELETE' }),
  previewDates: (rule) => {
    const params = new URLSearchParams();
    Object.entries(rule).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') params.append(k, v); });
    return makeRequest(`/recurring-orders/preview-dates?${params}`);
  },
  getPendingReview: () => makeRequest('/recurring-orders/pending-review'),
  approveInstance: (instanceId) => makeRequest(`/recurring-orders/instances/${instanceId}/approve`, { method: 'POST' }),
  skipInstance: (instanceId) => makeRequest(`/recurring-orders/instances/${instanceId}/skip`, { method: 'POST' }),
  updateInstance: (instanceId, data) => makeRequest(`/recurring-orders/instances/${instanceId}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  }),
};

// ============================================================================
// Events API - Wedding & Special Events Management
// ============================================================================
export const eventsApi = {
  // Core CRUD
  getAll: (params = {}) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.append('page', params.page);
    if (params.limit) searchParams.append('limit', params.limit);
    if (params.event_type) searchParams.append('event_type', params.event_type);
    if (params.status) searchParams.append('status', params.status);
    if (params.search) searchParams.append('search', params.search);
    if (params.date_from) searchParams.append('date_from', params.date_from);
    if (params.date_to) searchParams.append('date_to', params.date_to);
    if (params.sort_by) searchParams.append('sort_by', params.sort_by);
    if (params.sort_order) searchParams.append('sort_order', params.sort_order);
    return makeRequest(`/events?${searchParams}`);
  },
  getStats: () => makeRequest('/events/stats'),
  getById: (eventId) => makeRequest(`/events/${eventId}`),
  getConsultation: (eventId) => makeRequest(`/events/${eventId}/consultation`),
  getVision: (eventId) => makeRequest(`/events/${eventId}/vision`),
  updateVision: (eventId, data) => makeRequest(`/events/${eventId}/vision`, { method: 'PUT', body: JSON.stringify(data) }),
  getStemDetections: (eventId) => makeRequest(`/events/${eventId}/stem-detections`),
  updateStemDetection: (eventId, detectionId, body) =>
    makeRequest(`/events/${eventId}/stem-detections/${detectionId}`, { method: 'PUT', body: JSON.stringify(body) }),
  runStemDetection: (eventId) =>
    makeRequest(`/events/${eventId}/stem-detections/run`, { method: 'POST' }),
  create: (data) => makeRequest('/events', { method: 'POST', body: JSON.stringify(data) }),
  update: (eventId, data) => makeRequest(`/events/${eventId}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (eventId) => makeRequest(`/events/${eventId}`, { method: 'DELETE' }),

  // Install tickets
  getInstallBoard: (weeks = 6) => makeRequest(`/events/install-tickets/board?weeks=${weeks}`),
  printInstallTicket: (eventId) => makeRequest(`/events/${eventId}/install-ticket/print`, { method: 'POST' }),
  printWeekInstallTickets: (weekStart) => makeRequest('/events/install-tickets/print-week', { method: 'POST', body: JSON.stringify({ weekStart }) }),

  // Line items
  addLineItem: (eventId, item) => makeRequest(`/events/${eventId}/line-items`, { method: 'POST', body: JSON.stringify(item) }),
  updateLineItem: (eventId, itemId, data) => makeRequest(`/events/${eventId}/line-items/${itemId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteLineItem: (eventId, itemId) => makeRequest(`/events/${eventId}/line-items/${itemId}`, { method: 'DELETE' }),

  // Contacts
  addContact: (eventId, contact) => makeRequest(`/events/${eventId}/contacts`, { method: 'POST', body: JSON.stringify(contact) }),
  updateContact: (eventId, contactId, data) => makeRequest(`/events/${eventId}/contacts/${contactId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteContact: (eventId, contactId) => makeRequest(`/events/${eventId}/contacts/${contactId}`, { method: 'DELETE' }),

  // Timeline
  addTimelineEntry: (eventId, entry) => makeRequest(`/events/${eventId}/timeline`, { method: 'POST', body: JSON.stringify(entry) }),
  updateTimelineEntry: (eventId, entryId, data) => makeRequest(`/events/${eventId}/timeline/${entryId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTimelineEntry: (eventId, entryId) => makeRequest(`/events/${eventId}/timeline/${entryId}`, { method: 'DELETE' }),

  // Payments
  createPayment: (eventId, payment) => makeRequest(`/events/${eventId}/payments`, { method: 'POST', body: JSON.stringify(payment) }),
  updatePayment: (eventId, paymentId, data) => makeRequest(`/events/${eventId}/payments/${paymentId}`, { method: 'PUT', body: JSON.stringify(data) }),
  sendPaymentToShopify: (eventId, paymentId) => makeRequest(`/events/${eventId}/payments/${paymentId}/send-to-shopify`, { method: 'POST' }),
  sendPaymentConfirmationEmail: (eventId, paymentId, email) => makeRequest(`/events/${eventId}/payments/${paymentId}/send-confirmation-email`, { method: 'POST', body: JSON.stringify({ email }) }),
  sendDepositRequest: (eventId, amount) => makeRequest(`/events/${encodeURIComponent(eventId)}/payments/deposit-request`, {
    method: 'POST', body: JSON.stringify({ amount }),
  }),

  // Change requests
  respondToChangeRequest: (eventId, requestId, response) => makeRequest(`/events/${eventId}/change-requests/${requestId}`, { method: 'PUT', body: JSON.stringify(response) }),

  // Activity feed
  getActivity: (eventId) => makeRequest(`/events/${eventId}/activity`),
  postComment: (eventId, body) => makeRequest(`/events/${eventId}/activity/comment`, { method: 'POST', body: JSON.stringify({ body }) }),

  // Audit log
  getAuditLog: (eventId) => makeRequest(`/events/${eventId}/audit-log`),

  // Portal PIN
  resendPin: (eventId) => makeRequest(`/events/${eventId}/resend-pin`, { method: 'POST' }),

  // Bride vision questionnaire
  sendVisionQuestionnaire: (eventId) => makeRequest(`/events/${encodeURIComponent(eventId)}/vision-questionnaire/send`, { method: 'POST' }),

  // Booking packet (sign + deposit)
  sendBookingPacket: (eventId, amount) => makeRequest(`/events/${encodeURIComponent(eventId)}/booking-packet/send`, {
    method: 'POST', body: JSON.stringify({ amount }),
  }),

  // Dispatch bridge (manual "Send to dispatch" backstop)
  sendEventToDispatch: (eventId) => makeRequest(`/events/${eventId}/dispatch`, { method: 'POST' }),

  // Strike/flip/pickup work orders
  listWorkOrders: (eventId) => makeRequest(`/events/${eventId}/work-orders`),
  createWorkOrder: (eventId, body) => makeRequest(`/events/${eventId}/work-orders`, { method: 'POST', body: JSON.stringify(body) }),
  updateWorkOrder: (woId, body) => makeRequest(`/events/work-orders/${woId}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteWorkOrder: (woId) => makeRequest(`/events/work-orders/${woId}`, { method: 'DELETE' }),
  dispatchWorkOrder: (woId) => makeRequest(`/events/work-orders/${woId}/dispatch`, { method: 'POST' }),
  addWorkOrderItem: (woId, body) => makeRequest(`/events/work-orders/${woId}/items`, { method: 'POST', body: JSON.stringify(body) }),
  deleteWorkOrderItem: (itemId) => makeRequest(`/events/work-order-items/${itemId}`, { method: 'DELETE' }),
};

// Industry news API
export const newsApi = {
  getBriefing: () => makeRequest('/news/briefing'),
  getCostTrends: () => makeRequest('/news/cost-trends'),
  // Action items staff have closed with Done, shop-wide (sql/205).
  listAcks: () => makeRequest('/news/acks'),
  ack: (itemKey) => makeRequest('/news/acks', { method: 'POST', body: JSON.stringify({ itemKey }) }),
  unack: (itemKey) =>
    makeRequest(`/news/acks?itemKey=${encodeURIComponent(itemKey)}`, { method: 'DELETE' }),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => {
    // Store-timezone "today" so the dashboard's day matches the shop, not the browser.
    return makeRequest(`/dashboard/stats?date=${getTodayInTimezone()}`);
  },
};

// Payment Cards API (encrypted card storage with 2FA)
export const paymentCardsApi = {
  getCards: (customerId) => makeRequest(`/payment-cards/${customerId}/cards`),
  addCard: (customerId, cardData) => makeRequest(`/payment-cards/${customerId}/cards`, {
    method: 'POST',
    body: JSON.stringify(cardData),
  }),
  unlockCard: (customerId, cardId, totpCode) => makeRequest(`/payment-cards/${customerId}/cards/${cardId}/unlock`, {
    method: 'POST',
    body: JSON.stringify({ totpCode }),
  }),
  updateCard: (customerId, cardId, cardData, cardAccessToken) => makeRequest(`/payment-cards/${customerId}/cards/${cardId}`, {
    method: 'PUT',
    body: JSON.stringify(cardData),
    headers: { 'X-Card-Access-Token': cardAccessToken },
  }),
  deleteCard: (customerId, cardId, cardAccessToken) => makeRequest(`/payment-cards/${customerId}/cards/${cardId}`, {
    method: 'DELETE',
    headers: { 'X-Card-Access-Token': cardAccessToken },
  }),
  getAuditLog: (customerId) => makeRequest(`/payment-cards/${customerId}/cards/audit-log`),
};

// TOTP 2FA API
export const totpApi = {
  setup: () => makeRequest('/totp/setup', { method: 'POST' }),
  verifySetup: (code) => makeRequest('/totp/verify-setup', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }),
  verify: (code) => makeRequest('/totp/verify', {
    method: 'POST',
    body: JSON.stringify({ code }),
  }),
  getStatus: () => makeRequest('/totp/status'),
  remove: (userId) => makeRequest(`/totp/remove/${userId}`, { method: 'DELETE' }),
  updateCardAccess: (userId, canAccessCards) => makeRequest(`/totp/card-access/${userId}`, {
    method: 'PUT',
    body: JSON.stringify({ canAccessCards }),
  }),
};

// Deployments API (Settings → Updates tab)
export const deploymentsApi = {
  getCurrent: () => makeRequest('/deployments/current'),
  getReleaseNotes: () => makeRequest('/deployments/release-notes'),
};

// App Config API — small public endpoint returning server-side defaults the
// frontend bootstraps from (currently just the configured timezone).
export const appConfigApi = {
  get: () => makeRequest('/config'),
};

// Store Settings API (global business policies)
// - getPublic: read non-sensitive settings (incl. priority_time_fee_enabled). No auth required,
//   so POS mode and unauthenticated startup paths can read the toggle.
// - getAll: read everything (authenticated).
// - update: admin-only PUT; the route enforces it.
export const storeSettingsApi = {
  getPublic: () => makeRequest('/store-settings/public'),
  // Hours, GPS config and store_address; open to drivers as well as staff.
  getHours: () => makeRequest('/store-settings/hours'),
  getAll: () => makeRequest('/store-settings'),
  update: (key, value) => makeRequest(`/store-settings/${key}`, {
    method: 'PUT',
    body: JSON.stringify({ value }),
  }),
};

// Sales tax mode for online store orders (5D): flat rate or Stripe Tax.
// setMode('stripe') answers 409 STRIPE_TAX_NOT_READY (error.data.stripe) until Stripe is set up.
export const taxApi = {
  getSettings: () => makeRequest('/tax/settings'),
  setMode: (mode) => makeRequest('/tax/mode', { method: 'PUT', body: JSON.stringify({ mode }) }),
  // Stripe Tax mode: the order forms' tax ({ items, deliveryFee, priorityTimeFee,
  // fulfillmentType, recipient, exempt, orderNumber? }) → { status, tax, rate, message }.
  quote: (body) => makeRequest('/tax/quote', { method: 'POST', body: JSON.stringify(body) }),
};

// Stem Catalog API (Settings → Flower Catalog). Aliases + sourcing per stem type.
export const stemCatalogApi = {
  list: () => makeRequest('/stem-catalog'),
  update: (stemKey, body) => makeRequest(`/stem-catalog/${encodeURIComponent(stemKey)}`, { method: 'PUT', body: JSON.stringify(body) }),
};

// Production Pull Sheet API — weekly flower pull sheets for paid weddings
export const productionApi = {
  getWeeks: (horizonWeeks = 6) => makeRequest(`/production/weeks?horizonWeeks=${horizonWeeks}`),
  getPullSheet: (weekStart) => makeRequest(`/production/pull-sheet?weekStart=${encodeURIComponent(weekStart)}`),
  generate: (weekStart) => makeRequest('/production/pull-sheet/generate', { method: 'POST', body: JSON.stringify({ weekStart }) }),
  updateLine: (lineId, patch) => makeRequest(`/production/pull-sheet/lines/${encodeURIComponent(lineId)}`, { method: 'PUT', body: JSON.stringify(patch) }),
  orderVendor: (pullSheetId, vendor, orderedAt) => makeRequest(`/production/pull-sheet/${encodeURIComponent(pullSheetId)}/order-vendor`, { method: 'POST', body: JSON.stringify({ vendor, orderedAt }) }),
  getReadyCount: () => makeRequest('/production/ready-count'),
  // Phase 7 slice 3 — processing intake + vendor credit claims
  openIntake: (pullSheetId, vendorName, deliveryDate) => makeRequest(`/production/pull-sheet/${encodeURIComponent(pullSheetId)}/intakes`, { method: 'POST', body: JSON.stringify({ vendorName, deliveryDate }) }),
  updateIntakeLine: (intakeId, lineId, patch) => makeRequest(`/production/intakes/${encodeURIComponent(intakeId)}/lines/${encodeURIComponent(lineId)}`, { method: 'PUT', body: JSON.stringify(patch) }),
  completeIntake: (intakeId) => makeRequest(`/production/intakes/${encodeURIComponent(intakeId)}/complete`, { method: 'POST' }),
  getClaims: (status) => makeRequest(`/production/claims${status ? `?status=${encodeURIComponent(status)}` : ''}`),
  sendClaim: (claimId, opts = {}) => makeRequest(`/production/claims/${encodeURIComponent(claimId)}/send`, { method: 'POST', body: JSON.stringify(opts) }),
  resolveClaim: (claimId, status, resolutionNote) => makeRequest(`/production/claims/${encodeURIComponent(claimId)}/resolve`, { method: 'PUT', body: JSON.stringify({ status, resolutionNote }) }),
};

// Formula Calculator API — the price list lives in store_settings JSONB keys.
export const formulaApi = {
  getPriceList: async () => {
    const resp = await makeRequest('/store-settings');
    const data = resp?.data || {};
    const labor = data.formula_labor;
    return {
      vases: Array.isArray(data.formula_vases) ? data.formula_vases : [],
      flowers: Array.isArray(data.formula_flowers) ? data.formula_flowers : [],
      labor:
        labor && Array.isArray(labor.rates) && labor.rates.length
          ? labor
          : { rates: [22.5, 20], defaultRate: 22.5 },
    };
  },
  saveVases: (vases) => makeRequest('/store-settings/formula_vases', {
    method: 'PUT',
    body: JSON.stringify({ value: vases }),
  }),
  saveFlowers: (flowers) => makeRequest('/store-settings/formula_flowers', {
    method: 'PUT',
    body: JSON.stringify({ value: flowers }),
  }),
  saveLabor: (labor) => makeRequest('/store-settings/formula_labor', {
    method: 'PUT',
    body: JSON.stringify({ value: labor }),
  }),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('image', file);
    const token = localStorage.getItem('token');
    return fetch(`${BASE_URL}${API_PREFIX}/store-settings/upload-formula-image`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    }).then((res) => res.json());
  },
};

const api = {
  orders: ordersApi,
  customers: customersApi,
  products: productsApi,
  health: healthApi,
  auth: authApi,
  drafts: draftsApi,
  workTickets: workTicketsApi,
  events: eventsApi,
  request: apiRequest,
  handleError: handleApiError,
  getErrorMessage,
};

export default api;